import { useContext, useMemo } from "react";
import { useSelector } from "react-redux";

import { StoreType } from "../../../utils/types";
import { LOCATIONS } from "../../filters/constants";
import { FilterContext } from "../../filters/filter-context";
import { getLocalDateKey } from "../utils/holiday-dates";
import useLocationHolidays, {
	LocationHolidayOccurrence,
} from "./use-location-holidays";

/**
 * Holiday occurrences for the Flex Schedule's visible days, across the
 * locations the supervisor filters on (all of their locations when no
 * location filter is applied), grouped by "yyyy-MM-dd".
 */
const useSchedulerHolidays = (firstDay: Date | null, numberOfDays: number) => {
	const filterContext = useContext(FilterContext);
	const filterLocations = useSelector(
		(state: StoreType) => state.supervisor.filterData.allLocations
	);

	const selectedLocationIds = useMemo(() => {
		const appliedLocations = filterContext?.appliedFilters.find(
			(appliedFilter) => appliedFilter.type === LOCATIONS
		)?.data;
		if (appliedLocations && appliedLocations.length > 0) {
			return appliedLocations.map((location) => Number(location.id));
		}
		return (filterLocations ?? []).map((location) => location.id);
	}, [filterContext?.appliedFilters, filterLocations]);

	const { getOccurrencesInRange } = useLocationHolidays(selectedLocationIds);

	const fromKey = firstDay ? getLocalDateKey(firstDay) : "";
	const toKey = firstDay
		? getLocalDateKey(
			new Date(
				firstDay.getFullYear(),
				firstDay.getMonth(),
				firstDay.getDate() + numberOfDays - 1
			)
		)
		: "";

	return useMemo(() => {
		const byDateKey: Record<string, LocationHolidayOccurrence[]> = {};
		if (!fromKey || !toKey) return byDateKey;
		getOccurrencesInRange(fromKey, toKey).forEach((occurrence) => {
			const onDate = (byDateKey[occurrence.dateKey] ??= []);
			// A Company Holiday shows up once per location; list it once
			if (!onDate.some((other) => other.holiday.id === occurrence.holiday.id)) {
				onDate.push(occurrence);
			}
		});
		return byDateKey;
	}, [fromKey, toKey, getOccurrencesInRange]);
};

export default useSchedulerHolidays;
